import React, { useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import { monthName } from '../../utils/courseClosure';
import FileUpload from '../common/FileUpload';
import MapboxAutocomplete, { AddressComponents } from '../common/formElements/MapboxAutocomplete';
import MapboxMap from '../common/MapboxMap';
import { CourseDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation';
import { useCountries } from '../../hooks/useCountryApi';
import { useStates } from '../../hooks/useStateApi';
import { useSubRegions } from '../../hooks/useSubRegionApi';
import { useFileApi } from '../../hooks/useFileApi';
import { FileTypeConstants } from '../../constants/FileTypeConstants';

interface CourseFormProps {
  initialValues: CourseDTO;
  onSubmit: (data: CourseDTO) => void;
  onCancel: () => void;
  setLogoFile?: (file: File | null) => void;
  setHeaderImageFile?: (file: File | null) => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  logo: { id: 'logo', title: 'Logo' },
  headerImage: { id: 'header-image', title: 'Header Image' },
  basicInfo: { id: 'basic-info', title: 'Basic Information' },
  location: { id: 'location', title: 'Location' },
  contact: { id: 'contact', title: 'Contact' },
  clubManager: { id: 'club-manager', title: 'Club Manager' },
  proShop: { id: 'pro-shop', title: 'Pro Shop' },
  professional: { id: 'professional', title: 'Professional' },
  additional: { id: 'additional', title: 'Additional Details' },
} as const;

const validationRules: ValidationRules = {
  name: [{ required: true, message: 'Name is required' }],
  address: [{ required: true, message: 'Address is required' }],
};

const CourseForm: React.FC<CourseFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  setLogoFile,
  setHeaderImageFile,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CourseDTO>(initialValues);
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState<string | null>(null);
  const [selectedHeaderImageFile, setSelectedHeaderImageFile] = useState<File | null>(null);
  const [existingHeaderImageUrl, setExistingHeaderImageUrl] = useState<string | null>(null);

  const { errors, validate } = useFormValidation<CourseDTO>(validationRules);
  const { data: countries = [] } = useCountries();
  const { data: states = [] } = useStates();
  const { data: subRegions = [] } = useSubRegions();
  const { getFileUrl } = useFileApi();

  // Fetch the signed URL for an already-uploaded logo (only needed while no new file
  // has been picked locally — a fresh pick previews instantly via an object URL instead).
  useEffect(() => {
    if (!formData.logoFileId || selectedLogoFile) {
      setExistingLogoUrl(null);
      return;
    }

    let cancelled = false;
    getFileUrl(formData.logoFileId).then((response) => {
      if (!cancelled && response?.data?.url) {
        setExistingLogoUrl(response.data.url);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [formData.logoFileId, selectedLogoFile]);

  // Instant local preview for a freshly picked file, without waiting on an upload.
  const [selectedLogoPreviewUrl, setSelectedLogoPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedLogoFile) {
      setSelectedLogoPreviewUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(selectedLogoFile);
    setSelectedLogoPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedLogoFile]);

  const logoPreviewUrl = selectedLogoPreviewUrl || existingLogoUrl || undefined;

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedLogoFile(file);
    setLogoFile?.(file);
  };

  const handleLogoRemove = () => {
    setSelectedLogoFile(null);
    setLogoFile?.(null);
    setFormData((prev) => ({ ...prev, logoFileId: undefined }));
  };

  // Same pattern as the logo above: fetch the signed URL for an already-uploaded header
  // image (only while no new file has been picked locally), with an instant local preview
  // via an object URL for a freshly picked file.
  useEffect(() => {
    if (!formData.headerImageFileId || selectedHeaderImageFile) {
      setExistingHeaderImageUrl(null);
      return;
    }

    let cancelled = false;
    getFileUrl(formData.headerImageFileId).then((response) => {
      if (!cancelled && response?.data?.url) {
        setExistingHeaderImageUrl(response.data.url);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [formData.headerImageFileId, selectedHeaderImageFile]);

  const [selectedHeaderImagePreviewUrl, setSelectedHeaderImagePreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedHeaderImageFile) {
      setSelectedHeaderImagePreviewUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(selectedHeaderImageFile);
    setSelectedHeaderImagePreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedHeaderImageFile]);

  const headerImagePreviewUrl = selectedHeaderImagePreviewUrl || existingHeaderImageUrl || undefined;

  const handleHeaderImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedHeaderImageFile(file);
    setHeaderImageFile?.(file);
  };

  const handleHeaderImageRemove = () => {
    setSelectedHeaderImageFile(null);
    setHeaderImageFile?.(null);
    setFormData((prev) => ({ ...prev, headerImageFileId: undefined }));
  };

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const closedMonthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: String(i + 1),
    label: monthName(i + 1) ?? '',
  }));

  const countryOptions = countries
    .filter((c) => c.id)
    .map((c) => ({ value: c.id as string, label: c.name || '' }));

  const stateOptions = states
    .filter((s) => s.id && s.countryId === formData.countryId)
    .map((s) => ({ value: s.id as string, label: s.name || '' }));

  const subRegionOptions = subRegions
    .filter((sr) => sr.id && sr.countryId === formData.countryId)
    .map((sr) => ({ value: sr.id as string, label: sr.name || '' }));

  const handleCountryChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const countryId = e.target.value || undefined;
    setFormData((prev) => ({
      ...prev,
      countryId,
      // A state/sub region from the previous country no longer makes sense
      stateId: undefined,
      subRegionId: undefined,
    }));
  };

  const handleLocationSelect = (
    latitude: number,
    longitude: number,
    addressComponents?: AddressComponents,
  ) => {
    // Try to pre-select the Country the picked address is actually in, matching on
    // ISO code first (more reliable than the display name, e.g. "United States" vs "USA").
    const matchedCountry = addressComponents
      ? countries.find(
          (c) =>
            (addressComponents.countryCode &&
              c.code?.toLowerCase() === addressComponents.countryCode.toLowerCase()) ||
            (addressComponents.country &&
              c.name?.toLowerCase() === addressComponents.country.toLowerCase()),
        )
      : undefined;

    setFormData((prev) => ({
      ...prev,
      latitude,
      longitude,
      ...(matchedCountry
        ? {
            countryId: matchedCountry.id,
            // The previous state/sub region selection won't belong to the new country
            stateId: prev.countryId === matchedCountry.id ? prev.stateId : undefined,
            subRegionId: prev.countryId === matchedCountry.id ? prev.subRegionId : undefined,
          }
        : {}),
    }));
  };

  return (
    <Form sections={Object.values(FORM_SECTIONS)} onSave={handleSave} onCancel={onCancel} isSubmitting={isLoading}>
      <FormSection id={FORM_SECTIONS.logo.id} title={FORM_SECTIONS.logo.title}>
        <FileUpload
          id="logoFile"
          title="Upload or drag and drop course logo"
          accept={FileTypeConstants.IMAGES}
          onChange={handleLogoChange}
          previewUrl={logoPreviewUrl}
          onRemove={logoPreviewUrl ? handleLogoRemove : undefined}
        />
      </FormSection>

      <FormSection id={FORM_SECTIONS.headerImage.id} title={FORM_SECTIONS.headerImage.title}>
        <FileUpload
          id="headerImageFile"
          title="Upload or drag and drop course header image"
          accept={FileTypeConstants.IMAGES}
          onChange={handleHeaderImageChange}
          previewUrl={headerImagePreviewUrl}
          onRemove={headerImagePreviewUrl ? handleHeaderImageRemove : undefined}
        />
      </FormSection>

      <FormSection id={FORM_SECTIONS.basicInfo.id} title={FORM_SECTIONS.basicInfo.title} columns={2}>
        <FormField
          id="name"
          label="Name"
          type="text"
          value={formData.name || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Name"
          required
          error={errors.name}
        />
        <FormField
          id="active"
          label="Active"
          type="checkbox"
          value={formData.active || false}
          onChange={(e) => handleChange(e, setFormData)}
          error={errors.active}
        />
        {/* Closed: the course has shut for good. Everywhere else it then shows the shared Closed
            logo and "Closed <month> <year>"; the logo above is kept, and comes back if this is
            unticked. When it closed is a year and an optional month (see Course.Closed). */}
        <FormField
          id="closed"
          label="Closed"
          type="checkbox"
          value={formData.closed || false}
          onChange={(e) => handleChange(e, setFormData)}
          subtext="Shows the Closed logo and when it closed everywhere except this page. The logo above is kept."
          error={errors.closed}
        />
        {formData.closed && (
          <>
            <FormField
              id="closedYear"
              label="Closed Year"
              type="number"
              value={formData.closedYear ?? ''}
              onChange={(e) => {
                const year = Number.parseInt(e.target.value, 10);
                setFormData((prev) => ({
                  ...prev,
                  closedYear: Number.isNaN(year) ? undefined : year,
                }));
              }}
              placeholder="e.g. 2025 (leave empty if unknown)"
              error={errors.closedYear}
            />
            <FormField
              id="closedMonth"
              label="Closed Month"
              type="dropdown"
              value={
                formData.closedMonth != null ? String(formData.closedMonth) : ''
              }
              onChange={(e) => {
                const month = Number.parseInt(e.target.value, 10);
                setFormData((prev) => ({
                  ...prev,
                  closedMonth: Number.isNaN(month) ? undefined : month,
                }));
              }}
              options={closedMonthOptions}
              placeholder="Month unknown"
              disabled={formData.closedYear == null}
              error={errors.closedMonth}
            />
          </>
        )}
        <FormField
          id="totalHoles"
          label="Total Holes"
          type="number"
          value={formData.totalHoles ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Total Holes"
          error={errors.totalHoles}
        />
      </FormSection>

      <FormSection id={FORM_SECTIONS.location.id} title={FORM_SECTIONS.location.title} columns={1}>
        <MapboxAutocomplete
          fieldId="address"
          fieldLabel="Address"
          value={formData.address || ''}
          onChange={(e) => handleChange(e, setFormData)}
          onLocationSelect={handleLocationSelect}
          placeholder="Enter Address"
          required
          error={errors.address}
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            id="countryId"
            label="Country"
            type="dropdown"
            value={formData.countryId || ''}
            onChange={handleCountryChange}
            options={countryOptions}
            placeholder="Select a Country"
            error={errors.countryId}
          />
          <FormField
            id="stateId"
            label="State"
            type="dropdown"
            value={formData.stateId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            options={stateOptions}
            placeholder={formData.countryId ? 'Select a State' : 'Select a Country first'}
            disabled={!formData.countryId}
            error={errors.stateId}
          />
          <FormField
            id="subRegionId"
            label="Sub Region"
            type="dropdown"
            value={formData.subRegionId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            options={subRegionOptions}
            placeholder={formData.countryId ? 'Select a Sub Region' : 'Select a Country first'}
            disabled={!formData.countryId}
            error={errors.subRegionId}
          />
        </div>
        <MapboxMap
          latitude={formData.latitude}
          longitude={formData.longitude}
          onMarkerDragEnd={(latitude, longitude) => {
            setFormData((prev) => ({
              ...prev,
              latitude,
              longitude,
            }));
          }}
        />
      </FormSection>

      <FormSection id={FORM_SECTIONS.contact.id} title={FORM_SECTIONS.contact.title} columns={2}>
        <FormField
          id="phoneNumber"
          label="Phone Number"
          type="text"
          value={formData.phoneNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Phone Number"
          error={errors.phoneNumber}
        />
        <FormField
          id="email"
          label="Email"
          type="email"
          value={formData.email || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Email"
          error={errors.email}
        />
        <FormField
          id="url"
          label="Website Url"
          type="text"
          value={formData.url || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Url"
          error={errors.url}
        />
      </FormSection>

      <FormSection id={FORM_SECTIONS.clubManager.id} title={FORM_SECTIONS.clubManager.title} columns={2}>
        <FormField
          id="clubManagerName"
          label="Name"
          type="text"
          value={formData.clubManagerName || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Club Manager Name"
          error={errors.clubManagerName}
        />
        <FormField
          id="clubManagerEmail"
          label="Email"
          type="email"
          value={formData.clubManagerEmail || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Club Manager Email"
          error={errors.clubManagerEmail}
        />
        <FormField
          id="clubManagerPhoneNumber"
          label="Phone Number"
          type="text"
          value={formData.clubManagerPhoneNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Club Manager Phone Number"
          error={errors.clubManagerPhoneNumber}
        />
      </FormSection>

      <FormSection id={FORM_SECTIONS.proShop.id} title={FORM_SECTIONS.proShop.title} columns={2}>
        <FormField
          id="shopName"
          label="Name"
          type="text"
          value={formData.shopName || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Shop Name"
          error={errors.shopName}
        />
        <FormField
          id="shopEmail"
          label="Email"
          type="email"
          value={formData.shopEmail || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Shop Email"
          error={errors.shopEmail}
        />
        <FormField
          id="shopPhoneNumber"
          label="Phone Number"
          type="text"
          value={formData.shopPhoneNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Shop Phone Number"
          error={errors.shopPhoneNumber}
        />
      </FormSection>

      <FormSection id={FORM_SECTIONS.professional.id} title={FORM_SECTIONS.professional.title} columns={2}>
        <FormField
          id="proName"
          label="Name"
          type="text"
          value={formData.proName || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Pro Name"
          error={errors.proName}
        />
        <FormField
          id="proEmail"
          label="Email"
          type="email"
          value={formData.proEmail || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Pro Email"
          error={errors.proEmail}
        />
        <FormField
          id="proPhoneNumber"
          label="Phone Number"
          type="text"
          value={formData.proPhoneNumber || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Pro Phone Number"
          error={errors.proPhoneNumber}
        />
      </FormSection>

      <FormSection id={FORM_SECTIONS.additional.id} title={FORM_SECTIONS.additional.title}>
        <FormField
          id="details"
          label="Details"
          type="textarea"
          value={formData.details || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Details"
          error={errors.details}
        />
        <FormField
          id="timeZone"
          label="Time Zone"
          type="text"
          value={formData.timeZone || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Time Zone"
          error={errors.timeZone}
        />
        <FormField
          id="teeOffTimes"
          label="Tee Off Times"
          type="text"
          value={formData.teeOffTimes || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Tee Off Times"
          error={errors.teeOffTimes}
        />
        <FormField
          id="designerName"
          label="Designer Name"
          type="text"
          value={formData.designerName || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Designer Name"
          error={errors.designerName}
        />
        <FormField
          id="description"
          label="Description"
          type="textarea"
          value={formData.description || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Description"
          error={errors.description}
        />
        <FormField
          id="notes"
          label="Notes"
          type="textarea"
          value={formData.notes || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Notes"
          error={errors.notes}
        />
        <FormField
          id="websiteUrl"
          label="Website Url"
          type="text"
          value={formData.websiteUrl || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Website Url"
          error={errors.websiteUrl}
        />
        <FormField
          id="salesRep"
          label="Sales Rep"
          type="text"
          value={formData.salesRep || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Sales Rep"
          error={errors.salesRep}
        />
        <FormField
          id="drivingRange"
          label="Driving Range"
          type="checkbox"
          value={formData.drivingRange || false}
          onChange={(e) => handleChange(e, setFormData)}
          error={errors.drivingRange}
        />
        <FormField
          id="practiceArea"
          label="Practice Area"
          type="checkbox"
          value={formData.practiceArea || false}
          onChange={(e) => handleChange(e, setFormData)}
          error={errors.practiceArea}
        />
        <FormField
          id="isSociety"
          label="Is Society"
          type="checkbox"
          value={formData.isSociety || false}
          onChange={(e) => handleChange(e, setFormData)}
          error={errors.isSociety}
        />
        <FormField
          id="legacyCourseId"
          label="Legacy Course Id"
          type="number"
          value={formData.legacyCourseId ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Legacy Course Id"
          error={errors.legacyCourseId}
        />
      </FormSection>
    </Form>
  );
};

export default CourseForm;
